import React from 'react';
import { View, Text, StyleSheet, ViewStyle } from 'react-native';
import { formatCurrency } from '@/utils/common/formatting';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { PAYMENT_DESIGN_TOKENS, PAYMENT_TYPOGRAPHY, PAYMENT_SPACING } from '@/constants/residents/mobile/paymentDesignTokens';
import type { BalanceItem } from '@/types/residents/lease';

type Variant = 'desktop' | 'mobile' | 'marketplace';

interface ChargeBreakdownProps {
  balanceItems: BalanceItem[];
  balanceDue: number;
  variant?: Variant;
  style?: ViewStyle;
}

export const ChargeBreakdown: React.FC<ChargeBreakdownProps> = ({
  balanceItems,
  balanceDue,
  variant = 'desktop',
  style,
}) => {
  if (!balanceItems || balanceItems.length === 0) return null;
  if (balanceItems.every(item => item.balanceDue === 0)) return null;

  const s = variantStyles[variant];
  const isCredit = balanceDue < 0;
  const totalLabel = isCredit ? 'Account credit' : 'Total due';

  return (
    <View style={[s.section, style]}>
      <Text style={s.title}>{isCredit ? 'Credits' : 'Charges'}</Text>
      {balanceItems.map((item, index) => (
        <View key={`${item.chargeCode ?? item.description}-${index}`} style={s.row}>
          <Text style={s.description} numberOfLines={1}>
            {item.description}
          </Text>
          <Text style={s.amount}>{formatCurrency(item.balanceDue)}</Text>
        </View>
      ))}
      <View style={s.totalRow}>
        <Text style={s.totalLabel}>{totalLabel}</Text>
        <Text style={s.totalAmount}>{formatCurrency(balanceDue)}</Text>
      </View>
    </View>
  );
};

const desktopStyles = StyleSheet.create({
  section: {
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: 10,
    padding: 14,
    gap: 8,
  },
  title: {
    fontSize: 13,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 2,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  description: {
    fontSize: 13,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    flex: 1,
    marginRight: 12,
  },
  amount: {
    fontSize: 13,
    fontWeight: '500',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.08)',
    paddingTop: 8,
    marginTop: 2,
  },
  totalLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  totalAmount: {
    fontSize: 13,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
});

const mobileStyles = StyleSheet.create({
  section: {
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: 10,
    padding: 12,
    gap: 8,
  },
  title: {
    ...PAYMENT_TYPOGRAPHY.textXsMedium,
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
    marginBottom: 2,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  description: {
    ...PAYMENT_TYPOGRAPHY.bodySm,
    color: PAYMENT_DESIGN_TOKENS.TEXT_MUTED,
    flex: 1,
    marginRight: 12,
  },
  amount: {
    ...PAYMENT_TYPOGRAPHY.bodySmMedium,
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: PAYMENT_DESIGN_TOKENS.BORDER,
    paddingTop: 8,
    marginTop: 2,
  },
  totalLabel: {
    ...PAYMENT_TYPOGRAPHY.textXsMedium,
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
  },
  totalAmount: {
    ...PAYMENT_TYPOGRAPHY.textXsMedium,
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
  },
});

const marketplaceStyles = StyleSheet.create({
  section: {
    backgroundColor: MARKETPLACE_COLORS.inputBackground,
    borderRadius: 12,
    padding: 16,
    gap: 10,
  },
  title: {
    fontSize: 14,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    marginBottom: 4,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  description: {
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    flex: 1,
    marginRight: 12,
  },
  amount: {
    fontSize: 14,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textPrimary,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: MARKETPLACE_COLORS.inputBorder,
    paddingTop: 10,
    marginTop: 2,
  },
  totalLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
  },
  totalAmount: {
    fontSize: 14,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
  },
});

const variantStyles = {
  desktop: desktopStyles,
  mobile: mobileStyles,
  marketplace: marketplaceStyles,
} as const;
